import { useQuery } from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import { sameId, type WorkspaceClient } from './org-workspaces';
import { iamKeys } from './types';

const computeApi = getHttpClient('computeApi');

/**
 * Another organization's compute workspace that the caller reaches without belonging to it:
 * through a role on one of its projects, or a share of one of its machines. `metadata_id` is
 * what scopes compute calls to it (the service id of that organization's compute console); a
 * project role does not carry it the way a share of one machine does (`resource_service_id`).
 */
export interface SharedWorkspace {
  organization_id: string;
  name: string;
  metadata_id: string;
  project_ids?: string[];
  machine_ids?: string[];
}

/** The shared workspace a compute service id (its `metadata_id`) names, if any. */
export function findSharedWorkspace<W extends { metadata_id: string }>(
  workspaces: readonly W[],
  serviceId: string | undefined,
): W | undefined {
  return workspaces.find((w) => sameId(w.metadata_id, serviceId));
}

/** The key of Compute's own gate query (GET /workspace): the registry's adapter and `useComputeWorkspace` read the same entry. */
export const COMPUTE_WORKSPACE_KEY = ['compute', 'workspace'] as const;

/**
 * The workspace in an answer of GET /workspace. Two envelopes are live: the Rails-compatible `{status, data: {workspace}}`
 * and authz's `{message, result}`; an absent workspace (`result: null`) stays null. ONE reader for the gate hook and the adapter.
 */
export function readComputeWorkspace<T>(body: unknown): T | null {
  const b = body as { data?: { workspace?: T | null } | null; result?: T | null } | undefined;
  return b?.data?.workspace ?? b?.result ?? null;
}

/** GET /workspaces/shared of @compute: empty when compute does not enforce IAM or nothing is granted. ONE reader for the hook and the adapter. */
export async function fetchSharedWorkspaces(client: WorkspaceClient): Promise<SharedWorkspace[]> {
  const res = (await client.get('/workspaces/shared')).data as { data?: { workspaces?: SharedWorkspace[] } } | undefined;
  return res?.data?.workspaces ?? [];
}

/** GET /workspaces/shared of @compute. */
export function useSharedWorkspaces() {
  return useQuery({
    queryKey: iamKeys.mySharedWorkspaces,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => fetchSharedWorkspaces(computeApi),
  });
}
